import type { HotelSearchValue } from '@voll-intelligence/intelligence-ui-kit';
import { fmtTripRange, fmtWeekdayDayMonth } from '../../../shared/ui/flight_format.js';
import { genUiT } from '../../../shared/ui/i18n.js';
import {
  nightsBetween,
  exceedsHotelMaxStay,
  HOTEL_MAX_STAY_NIGHTS,
} from '@voll-intelligence/genui-contract';

/*
 * As FRASES de data da busca de hotel — a pílula do QUANDO, o resumo do
 * rascunho no pé do calendário, o título da página de resultados. Saíram de
 * `hotel_planner_view.ts` (congelado no teto de linhas) quando o resumo passou
 * a falar a régua do aéreo (#3446).
 */

type HotelDates = Pick<HotelSearchValue, 'checkin' | 'checkout'>;

/* Exportada para o título da página determinística ("Hotéis em {lugar} · 9–11 set"):
   o vão SEM a contagem de noites — as noites moram no contexto da direita. */
export function hotelDateRangeLabel(value: HotelDates, locale: string): string | undefined {
  if (!value.checkin || !value.checkout) return undefined;
  return fmtTripRange(value.checkin, value.checkout, locale);
}

/* O resumo do RASCUNHO no pé do calendário segue a régua do aéreo (#3446 —
   "Qua, 21 out → Sex, 23 out · 2 noites", e "Qua, 21 out · escolha a saída"
   com só a entrada): os três seletores de data do produto falam a mesma frase.
   A cidade e a hora ficam de fora — aqui só há estadia. */
export function hotelDateDraftSummary(value: HotelDates, locale: string): string {
  if (!value.checkin) return genUiT(locale, 'hotelPlanner.chooseStayDates');
  if (!value.checkout) {
    return genUiT(locale, 'hotelPlanner.chooseCheckout', {
      date: fmtWeekdayDayMonth(value.checkin, locale),
    });
  }
  const stay = [value.checkin, value.checkout];
  const range = `${fmtWeekdayDayMonth(value.checkin, locale, [])} → ${fmtWeekdayDayMonth(value.checkout, locale, stay)}`;
  const summary = `${range} · ${hotelNightsLabel(value.checkin, value.checkout, locale)}`;
  // Acima do teto o Buscar trava (a seleção não passa no schema): o resumo diz
  // por quê, no lugar em que a pessoa está escolhendo a saída (#5315).
  if (!exceedsHotelMaxStay({ checkin: value.checkin, checkout: value.checkout })) return summary;
  return `${summary} · ${genUiT(locale, 'hotelPlanner.maxStayNights', { count: HOTEL_MAX_STAY_NIGHTS })}`;
}

/* A pílula do QUANDO (e o resumo da busca nos resultados e no compartilhar):
   a faixa da pílula do aéreo ("21–23 out", `fmtTripRange`) mais a contagem de
   noites, que no hotel é o dado da estadia. */
export function hotelSearchDateSummary(value: HotelSearchValue, locale: string): string {
  if (!value.checkin || !value.checkout) return genUiT(locale, 'hotelPlanner.datesTBD');
  const range = fmtTripRange(value.checkin, value.checkout, locale);
  return `${range} · ${hotelNightsLabel(value.checkin, value.checkout, locale)}`;
}

function hotelNightsLabel(checkin: string, checkout: string, locale: string): string {
  const nights = nightsBetween(checkin, checkout);
  return genUiT(locale, nights === 1 ? 'hotelPlanner.nights_one' : 'hotelPlanner.nights_other', {
    count: nights,
  });
}
